import HelpDrawer from '@/components/HelpDrawer';
import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spinner, initAnalytics, resolveAnalytics } from '@easydent/shared-ui';
import AppCookieBanner from '@/components/AppCookieBanner';
import AiAssistant from '@/components/AiAssistant';
import { useAuthStore } from '@/stores/authStore';
import { tryRestoreSession } from '@/lib/apiClient';

// Lazy-loaded pages
const LoginPage = React.lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = React.lazy(() => import('@/pages/auth/RegisterPage'));
const AcceptInvitePage = React.lazy(() => import('@/pages/auth/AcceptInvitePage'));
const ForgotPasswordPage = React.lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('@/pages/auth/ResetPasswordPage'));
const DemoLoginPage = React.lazy(() => import('@/pages/auth/DemoLoginPage'));
const DashboardPage = React.lazy(() => import('@/pages/dashboard/DashboardPage'));
const CalendarPage = React.lazy(() => import('@/pages/calendar/CalendarPage'));
const DoctorSchedulePage = React.lazy(() => import('@/pages/doctors/DoctorSchedulePage'));
const PatientsListPage = React.lazy(() => import('@/pages/patients/PatientsListPage'));
const PatientDetailPage = React.lazy(() => import('@/pages/patients/PatientDetailPage'));
const DuplicatesPage = React.lazy(() => import('@/pages/patients/DuplicatesPage'));
const ConsultationViewPage = React.lazy(() => import('@/pages/consultations/ConsultationViewPage'));
const SterilizationPage = React.lazy(() => import('@/pages/sterilization/SterilizationPage'));
const TreatmentCatalogPage = React.lazy(() => import('@/pages/treatments/TreatmentCatalogPage'));
const ReportsPage = React.lazy(() => import('@/pages/reports/ReportsPage'));
const InventoryPage = React.lazy(() => import('@/pages/inventory/InventoryPage'));
const SupplierInvoicesPage = React.lazy(() => import('@/pages/inventory/SupplierInvoicesPage'));
const PartnerAccountsPage = React.lazy(() => import('@/pages/partners/PartnerAccountsPage'));
const CampaignsPage = React.lazy(() => import('@/pages/campaigns/CampaignsPage'));
const AuditLogPage = React.lazy(() => import('@/pages/audit/AuditLogPage'));
const StaffPage = React.lazy(() => import('@/pages/staff/StaffPage'));
const MySchedulePage = React.lazy(() => import('@/pages/staff/MySchedulePage'));
const TeamSchedulePage = React.lazy(() => import('@/pages/staff/TeamSchedulePage'));
const ProfilePage = React.lazy(() => import('@/pages/account/ProfilePage'));
const SettingsPage = React.lazy(() => import('@/pages/settings/SettingsPage'));
const SupportPage = React.lazy(() => import('@/pages/support/SupportPage'));
const LegalPage = React.lazy(() => import('@/pages/legal/LegalPage'));

const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <Spinner size="lg" />
  </div>
);

// `deny` keeps a role off a route entirely — for clinical pages the API 403s anyway, so without
// it the page renders as a shell of failed requests rather than an honest "not for you".
/**
 * `deny` lists who is turned away; `allow` lists who is let in.
 *
 * Both exist because they fail in opposite directions, and which one is right depends on the page.
 * A deny-list admits any role added later by default — fine for a page most people should see, wrong
 * for one gated to owners. Support used a deny-list while its nav entry used an allow-list, so a new
 * role would have reached the page without ever seeing the link to it.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode; deny?: string[]; allow?: string[] }> = ({ children, deny, allow }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.role);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allow && (!role || !allow.includes(role))) return <Navigate to="/" replace />;
  if (deny && role && deny.includes(role)) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const App: React.FC = () => {
  // The access token lives in memory only — on a fresh page load, try to
  // restore the session from the HttpOnly refresh cookie before routing,
  // otherwise ProtectedRoute would bounce a valid session to /login.
  const [authReady, setAuthReady] = useState(false);

  // The assistant is mounted here rather than inside AppLayout, and that placement is load-bearing.
  //
  // AppLayout is not a router layout route — every page imports it and wraps its own JSX — so
  // anything mounted inside it is torn down and rebuilt on every navigation. The assistant kept its
  // conversation in local state, which meant asking a question, walking to the calendar to look
  // something up, and coming back to an empty panel. `assistantUiStore` exists only because
  // `open` and `tab` had to be rescued from that.
  //
  // Out here it mounts once for the session. Gated on the session rather than rendered blindly,
  // because this sits outside <Routes> and would otherwise appear over /login and /register.
  const signedIn = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    tryRestoreSession().finally(() => setAuthReady(true));
  }, []);

  // Consent-gated analytics. Stays off until this app has a website id AND the visitor consents.
  useEffect(() => {
    initAnalytics(resolveAnalytics('clinic'));
  }, []);

  if (!authReady) return <PageLoader />;

  return (
  <>
  <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      {/* Reached by someone who cannot sign in, so both stay outside the auth guard. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/demo" element={<DemoLoginPage />} />
      <Route path="/legal/:slug" element={<LegalPage />} />

      {/* Protected */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <DoctorSchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients"
        element={
          <ProtectedRoute>
            <PatientsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients/duplicates"
        element={
          <ProtectedRoute>
            <DuplicatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patients/:id"
        element={
          <ProtectedRoute>
            <PatientDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultations/:id"
        element={
          <ProtectedRoute deny={['Receptionist']}>
            <ConsultationViewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sterilization"
        element={
          <ProtectedRoute>
            <SterilizationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/treatments"
        element={
          <ProtectedRoute>
            <TreatmentCatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <InventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-invoices"
        element={
          <ProtectedRoute>
            <SupplierInvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <CampaignsPage />
          </ProtectedRoute>
        }
      />
      {/* What partners owe the clinic. Denied to Receptionist: they see a patient's balance to
          collect at checkout, but what the clinic earns commercially is not their screen. */}
      <Route
        path="/partner-accounts"
        element={
          <ProtectedRoute deny={['Receptionist', 'Doctor']}>
            <PartnerAccountsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff"
        element={
          <ProtectedRoute>
            <StaffPage />
          </ProtectedRoute>
        }
      />
      {/* Both forms: the list, and the deep link a support notification points at.
          An ALLOW-list, matching the nav entry and the controller's [Authorize(Roles = "TenantAdmin")].
          The deny-list this replaces agreed with them for today's five roles and would have silently
          disagreed for the sixth. */}
      <Route
        path="/support"
        element={
          <ProtectedRoute allow={['TenantAdmin']}>
            <SupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support/:ticketId"
        element={
          <ProtectedRoute allow={['TenantAdmin']}>
            <SupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-schedule"
        element={
          <ProtectedRoute>
            <MySchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-schedule"
        element={
          <ProtectedRoute>
            <TeamSchedulePage />
          </ProtectedRoute>
        }
      />
      {/* Approvals live inside the team schedule board, next to the coverage a decision changes.
          The old standalone page was a second copy of the same queue — keep the link working. */}
      <Route path="/approvals" element={<Navigate to="/team-schedule" replace />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  </Suspense>
  {signedIn && <AiAssistant />}
  {/* One instance, mounted here for the same reason as the assistant: several places open it —
      the header, a guidance suggestion, the answer the assistant just cited — and no single page
      owns it. */}
  {signedIn && <HelpDrawer />}
  <AppCookieBanner />
  </>
  );
};

export default App;
